import { ChatBuyRequestCard } from './ChatBuyRequestCard';
import { useState } from 'react';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
  MessageResponse,
} from '@/components/ai-elements/message';
import { AIThinkingLabel } from '@/components/ai/AIThinkingLabel';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AlertCircle, BookmarkCheck, Check, Copy, RotateCcw } from 'lucide-react';
import type { AskMessage } from './useAskCannafaxChat';
import { AnswerFeedback } from './AnswerFeedback';
import { useLocation } from 'react-router-dom';
import { EntityLink } from './EntityLink';
import { ToolStatusLine } from './ToolStatusLine';
import type { ToolStatus } from './streamChat';
import { cleanAssistantMarkdown } from '@/lib/entityLinks';
import { ChatImportCard } from './ChatImportCard';
import { ChatDraftCard } from './ChatDraftCard';
import { ChatMemoryForgetCard } from './ChatMemoryForgetCard';

// Buyer companies and listings named in answers become a contact card / listing link.
const ENTITY_COMPONENTS = { a: EntityLink } as never;

interface Props {
  messages: AskMessage[];
  isStreaming: boolean;
  error: string | null;
  onRetry: () => void;
  emptyState: React.ReactNode;
  threadId?: string | null;
  hideMessage?: (message: AskMessage, index: number) => boolean;
  /** Names the task while waiting (e.g. preparing today's brief); quips become the sub-line. */
  workingTitle?: string;
  /** Live connector/tool status from the stream; shown as a small line under the thinking label. */
  toolStatus?: ToolStatus | null;
}

function CopyAction({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <MessageAction
      tooltip={copied ? 'Copied' : 'Copy'}
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </MessageAction>
  );
}

export function AskCannafaxConversation({
  messages,
  isStreaming,
  error,
  onRetry,
  emptyState,
  threadId = null,
  hideMessage,
  workingTitle,
  toolStatus = null,
}: Props) {
  const location = useLocation();
  const last = messages[messages.length - 1];
  const awaitingFirstToken = isStreaming && last?.role !== 'assistant';

  if (messages.length === 0 && !isStreaming && !error) {
    return <div className="flex-1 overflow-y-auto">{emptyState}</div>;
  }

  return (
    <Conversation className="flex-1">
      <ConversationContent className="gap-6 px-4 py-5">
        {messages.map((msg, i) => hideMessage?.(msg, i) ? null : (
          <Message key={msg.id ?? i} from={msg.role}>
            <MessageContent
              className={cn(
                'leading-relaxed',
                'group-[.is-user]:bg-primary group-[.is-user]:text-primary-foreground',
              )}
            >
              {msg.role === 'assistant' ? (
                <MessageResponse components={ENTITY_COMPONENTS} className="[&_ol]:list-outside [&_ol]:pl-6 [&_ul]:list-outside [&_ul]:pl-6 [&_li>p:first-child]:inline [&_li>p:first-child]:m-0">{cleanAssistantMarkdown(msg.content)}</MessageResponse>
              ) : (
                <span className="whitespace-pre-wrap">{msg.content}</span>
              )}
            </MessageContent>
            {msg.role === 'assistant' && msg.draftProposal && (
              <ChatDraftCard proposal={msg.draftProposal} coaFile={msg.draftProposal.coaFile} threadId={threadId} />
            )}
            {msg.role === 'assistant' && msg.buyRequestProposal && (
              <ChatBuyRequestCard proposal={msg.buyRequestProposal} threadId={threadId} />
            )}
            {msg.role === 'assistant' && msg.importProposal && (
              <ChatImportCard proposal={msg.importProposal} file={msg.importProposal.file} threadId={threadId} />
            )}
            {msg.role === 'assistant' && msg.memoryForgetProposal && (
              <ChatMemoryForgetCard proposal={msg.memoryForgetProposal} threadId={threadId} />
            )}
            {msg.role === 'assistant' && !!msg.savedMemories?.length && (
              <div className="flex flex-wrap items-center gap-1.5" aria-live="polite">
                {msg.savedMemories.map((m) => (
                  <span
                    key={m.id}
                    title="Saved to your memory"
                    className="remembered-pill inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs"
                  >
                    <BookmarkCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="truncate">Remembered: {m.content}</span>
                  </span>
                ))}
              </div>
            )}
            {msg.role === 'assistant' && msg.content && !(isStreaming && i === messages.length - 1) && (
              <MessageActions className="flex-wrap opacity-60 transition-opacity group-hover:opacity-100">
                <CopyAction text={msg.content} />
                <AnswerFeedback
                  key={`${threadId}-${i}`}
                  question={[...messages.slice(0, i)].reverse().find((m) => m.role === 'user')?.content ?? ''}
                  answer={msg.content}
                  threadId={threadId}
                  route={location.pathname}
                />
              </MessageActions>
            )}
          </Message>
        ))}

        {(awaitingFirstToken || (isStreaming && toolStatus)) && (
          <div className="flex flex-col gap-1 text-sm text-muted-foreground">
            <AIThinkingLabel context={workingTitle ? 'brief' : 'chat'} title={workingTitle} />
            {toolStatus && <ToolStatusLine status={toolStatus} />}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p>{error}</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-1 h-7 px-2 text-xs text-destructive hover:bg-destructive/10"
                onClick={onRetry}
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Try again
              </Button>
            </div>
          </div>
        )}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
}